import { useMemo } from "react";
import { Paragraph, type TableColumnType } from "@ir/ui";
import {
  MasterDataProductPrice,
  type IMasterDataProductPrice,
  type MasterDataProductPrice as MasterDataProductPriceEntity,
} from "@src/domain/entities/masterData";
import {
  formatMasterDataCellText,
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  MasterDataListTable,
  renderMasterDataStatusChip,
  renderMasterDataTextCell,
} from "./masterDataTable.shared";

export interface MasterDataProductPriceRow extends IMasterDataProductPrice {
  key: string;
}

function mapPricesToRows(
  prices: MasterDataProductPriceEntity[],
): MasterDataProductPriceRow[] {
  return prices.map((entity) => ({
    ...MasterDataProductPrice.toJSON(entity),
    key: String(entity.productPriceId),
  }));
}

function renderBasePriceCell(value: unknown) {
  return (
    <Paragraph className="m-0">
      {typeof value === "number"
        ? value.toFixed(2)
        : formatMasterDataCellText(value)}
    </Paragraph>
  );
}

function buildColumns(): TableColumnType<MasterDataProductPriceRow>[] {
  return [
    {
      title: "Product Name",
      dataIndex: "itemName",
      key: "itemName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 260,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Product Key",
      dataIndex: "productKey",
      key: "productKey",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 180,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Base Price",
      dataIndex: "basePrice",
      key: "basePrice",
      align: "right",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 140,
      cellRender: renderBasePriceCell,
    },
    {
      title: "Start",
      dataIndex: "startDate",
      key: "startDate",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 140,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "End",
      dataIndex: "endDate",
      key: "endDate",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 140,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 100,
      cellRender: renderMasterDataStatusChip,
    },
  ];
}

interface MasterDataProductPricesTableProps {
  productPrices: MasterDataProductPriceEntity[];
  search: string;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
}

export const MasterDataProductPricesTable = ({
  productPrices,
  search,
  setSearch,
  setPage,
}: MasterDataProductPricesTableProps) => {
  const columns = useMemo(() => buildColumns(), []);
  const dataSource = useMemo(
    () => mapPricesToRows(productPrices),
    [productPrices],
  );

  return (
    <MasterDataListTable
      columns={columns}
      dataSource={dataSource}
      search={search}
      entityLabel="product prices"
      ariaLabel="Product Prices table"
      setSearch={setSearch}
      setPage={setPage}
    />
  );
};
